import React, { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, X, CheckCircle2, Clock, AlertCircle, Flag } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AssetProgressCard } from './AssetProgressCard';
import { AssignWorkItemDialog } from './AssignWorkItemDialog';
import { AddCommentDialog } from './AddCommentDialog';
import { AssetDetailsDialog } from './AssetDetailsDialog';

import { useToast } from '@/hooks/use-toast';
import type { AssetAuditProgress } from '@/types/audit';
import { demoDataService } from '@/services/demo/demo-data.service';
import { AuditActivityType } from '@/types/asset-audit';
import { auditDataService } from '@/services/audit-data-service';

// Helper function to check if asset matches search query - extracted to reduce cognitive complexity (S3776)
function assetMatchesSearch(asset: AssetAuditProgress, searchQuery: string): boolean {
  if (searchQuery === '') return true;
  const query = searchQuery.toLowerCase();
  return (
    asset.assetCode.toLowerCase().includes(query) ||
    asset.assetName.toLowerCase().includes(query) ||
    asset.currentLocation.toLowerCase().includes(query) ||
    asset.category.toLowerCase().includes(query)
  );
}

// Helper function to check if asset matches all filters - extracted to reduce cognitive complexity (S3776)
function assetMatchesFilters(
  asset: AssetAuditProgress,
  statusFilter: string,
  conditionFilter: string,
  locationFilter: string
): boolean {
  const matchesStatus = statusFilter === 'all' || asset.status === statusFilter;
  const matchesCondition = conditionFilter === 'all' || asset.condition === conditionFilter;
  const matchesLocation = locationFilter === 'all' || asset.currentLocation === locationFilter;
  return matchesStatus && matchesCondition && matchesLocation;
}

interface AssetProgressTabProps {
  auditId: string;
}

export function AssetProgressTab({ auditId }: Readonly<AssetProgressTabProps>) {
  const { toast } = useToast();
  
  const [assets, setAssets] = useState<AssetAuditProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [conditionFilter, setConditionFilter] = useState<string>('all');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [activeMetricFilter, setActiveMetricFilter] = useState<string | null>(null);
  
  // Dialog states
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [isWorkItemDialogOpen, setIsWorkItemDialogOpen] = useState(false);
  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

  // Load audit items from API
  React.useEffect(() => {
    const loadAuditItems = async () => {
      setLoading(true);
      try {
        // Always use auditDataService which handles demo/live mode internally
        const result = await auditDataService.getAuditItems(auditId, 1, 1000);
        if (result?.items && result.items.length > 0) {
          // Convert audit items to AssetAuditProgress format (without fetching asset details)
          const progressAssets = result.items.map((item: any) => ({
            id: item.id,
            assetCode: item.assetCode || item.assetTag || 'N/A',
            assetName: item.assetName || 'Unknown Asset',
            category: item.category || 'Uncategorised',
            status: item.status || 'not-started',
            condition: item.condition,
            currentLocation: item.currentLocation || item.expectedLocation || item.location || 'Unknown',
            previousLocation: item.previousLocation,
            locationStatus: item.locationStatus || 'pending',
            notes: item.notes,
            photos: item.photos ?? [],
            history: item.history ?? [],
            lastUpdated: item.lastUpdated || item.updatedAt || new Date().toISOString(),
            flaggedForReview: item.flaggedForReview || item.isFlagged || false,
            // Asset details will be fetched on-demand when user clicks "View Details"
            asset: undefined,
          }));
          
          setAssets(progressAssets);
        } else {
          // No items returned from API
          setAssets([]);
        }
      } catch (error) {
        console.error('Error loading audit items:', error);
        setAssets([]);
      } finally {
        setLoading(false);
      }
    };

    loadAuditItems();
  }, [auditId]);



  // Get unique locations for filter
  const uniqueLocations = useMemo(() => {
    const locations = new Set(assets.map(a => a.currentLocation));
    return Array.from(locations).sort((a, b) => a.localeCompare(b));
  }, [assets]);

  // Calculate summary metrics using standardized status values
  const metrics = useMemo(() => {
    return {
      total: assets.length,
      verified: assets.filter(a => a.status === 'verified').length,
      inProgress: assets.filter(a => a.status === 'in-progress').length,
      notStarted: assets.filter(a => a.status === 'not-started').length,
      flagged: assets.filter(a => a.status === 'flagged').length,
      missing: assets.filter(a => a.status === 'missing').length,
    };
  }, [assets]);

  // Filter assets using extracted helper functions to reduce cognitive complexity (S3776)
  const filteredAssets = useMemo(() => {
    return assets.filter(asset => {
      return assetMatchesSearch(asset, searchQuery) && 
             assetMatchesFilters(asset, statusFilter, conditionFilter, locationFilter);
    });
  }, [assets, searchQuery, statusFilter, conditionFilter, locationFilter]);

  const handleFlagForReview = (assetId: string, reason?: string) => {
    const asset = assets.find(a => a.id === assetId);
    const isFlagging = !asset?.flaggedForReview;
    
    // Create history entry
    const flagDescription = reason ? 'Asset flagged for review: ' + reason : 'Asset flagged for review';
    const historyEntry = {
      id: 'history-' + Date.now(),
      timestamp: new Date().toISOString(),
      action: isFlagging ? 'flagged' : 'unflagged',
      description: isFlagging ? flagDescription : 'Asset unflagged',
      user: 'Current User', // TODO: Get actual logged-in user
    };
    
    // Update asset state - DO NOT update notes field
    setAssets(prev =>
      prev.map(a =>
        a.id === assetId
          ? {
              ...a,
              flaggedForReview: isFlagging,
              status: isFlagging ? 'flagged' as const : 'not-started' as const,
              history: [...(a.history ?? []), historyEntry],
              lastUpdated: new Date().toISOString(),
            }
          : a
      )
    );

    // Add activity log to audit history
    try {
      const data = demoDataService.getData();
      const currentUser = data.users[0]; // TODO: Get actual logged-in user
      const timestamp = new Date().toISOString();
      
      const assetInfo = asset?.assetCode + ' (' + asset?.assetName + ')';
      const activityDescription = isFlagging 
        ? 'Asset ' + assetInfo + ' flagged for review' + (reason ? ': ' + reason : '')
        : 'Asset ' + assetInfo + ' unflagged';
      const newActivityLog = {
        id: 'activity-' + Date.now(),
        auditId: auditId,
        activityType: isFlagging ? AuditActivityType.ExceptionRaised : AuditActivityType.ExceptionResolved,
        description: activityDescription,
        userId: currentUser.id,
        userName: currentUser.name,
        performedBy: currentUser.name,
        userAvatar: currentUser.avatar,
        timestamp: timestamp,
        metadata: {
          assetId: assetId,
          assetCode: asset?.assetCode,
          assetName: asset?.assetName,
          action: isFlagging ? 'flagged' : 'unflagged',
          reason: reason ?? undefined,
        },
      };

      // Update demo data with new activity log
      demoDataService.updateData({
        activityLogs: [...data.activityLogs, newActivityLog],
      });
    } catch (error) {
      console.error('Failed to add activity log:', error);
    }
    
    toast({
      title: isFlagging ? 'Asset Flagged for Review' : 'Asset Unflagged',
      description: isFlagging
        ? `${asset?.assetCode} has been flagged for review.`
        : `${asset?.assetCode} has been unflagged.`,
    });
  };

  const handleAssignWorkItem = (assetId: string) => {
    setSelectedAssetId(assetId);
    setIsWorkItemDialogOpen(true);
  };

  const handleAddComment = (assetId: string) => {
    setSelectedAssetId(assetId);
    setIsCommentDialogOpen(true);
  };

  const handleViewDetails = (assetId: string) => {
    setSelectedAssetId(assetId);
    setIsDetailsDialogOpen(true);
  };



  const handleWorkItemSubmit = (_data: unknown) => {
    const asset = assets.find(a => a.id === selectedAssetId);
    toast({
      title: 'Work Item Assigned',
      description: `Work item created for ${asset?.assetCode}`,
    });
    setIsWorkItemDialogOpen(false);
    setSelectedAssetId(null);
  };

  const handleCommentSubmit = (comment: string) => {
    const asset = assets.find(a => a.id === selectedAssetId);
    
    if (!asset) return;

    // Get current user and timestamp
    const currentUser = 'Current User'; // TODO: Get actual logged-in user
    const timestamp = new Date().toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    // Create the comment note with user and timestamp
    const commentNote = `${comment} - ${currentUser}, ${timestamp}`;

    // Append to existing notes or create new notes
    const updatedNotes = asset.notes 
      ? `${asset.notes}\n\n${commentNote}`
      : commentNote;

    // Create history entry for the comment
    const historyEntry = {
      id: `history-${Date.now()}`,
      timestamp: new Date().toISOString(),
      action: 'comment',
      description: comment,
      user: currentUser,
    };

    // Update asset state with new notes and history entry
    setAssets(prev =>
      prev.map(a =>
        a.id === selectedAssetId
          ? {
              ...a,
              notes: updatedNotes,
              history: [...(a.history ?? []), historyEntry],
              lastUpdated: new Date().toISOString(),
            }
          : a
      )
    );

    // Add activity log to audit history
    try {
      const data = demoDataService.getData();
      const currentUser = data.users[0]; // TODO: Get actual logged-in user
      const timestamp = new Date().toISOString();
      
      const newActivityLog = {
        id: `activity-${Date.now()}`,
        auditId: auditId,
        activityType: AuditActivityType.CommentAdded,
        description: `Comment added to ${asset.assetCode} (${asset.assetName}): ${comment}`,
        userId: currentUser.id,
        userName: currentUser.name,
        performedBy: currentUser.name,
        userAvatar: currentUser.avatar,
        timestamp: timestamp,
        metadata: {
          assetId: selectedAssetId,
          assetCode: asset.assetCode,
          assetName: asset.assetName,
          comment: comment,
        },
      };

      // Update demo data with new activity log
      demoDataService.updateData({
        activityLogs: [...data.activityLogs, newActivityLog],
      });
    } catch (error) {
      console.error('Failed to add activity log:', error);
    }

    toast({
      title: 'Comment Added',
      description: `Comment added to ${asset.assetCode}`,
    });
    
    setIsCommentDialogOpen(false);
    setSelectedAssetId(null);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setConditionFilter('all');
    setLocationFilter('all');
    setActiveMetricFilter(null);
  };

  const handleMetricClick = (metricType: string) => {
    // If clicking the same metric, clear the filter
    if (activeMetricFilter === metricType) {
      setStatusFilter('all');
      setActiveMetricFilter(null);
    } else {
      // Set the appropriate status filter based on metric type
      switch (metricType) {
        case 'total':
          setStatusFilter('all');
          break;
        case 'verified':
          setStatusFilter('verified');
          break;
        case 'in-progress':
          setStatusFilter('in-progress');
          break;
        case 'not-started':
          setStatusFilter('not-started');
          break;
        case 'flagged':
          setStatusFilter('flagged');
          break;
        case 'missing':
          setStatusFilter('missing');
          break;
        default:
          setStatusFilter('all');
      }
      setActiveMetricFilter(metricType);
    }
  };

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || conditionFilter !== 'all' || locationFilter !== 'all';

  const selectedAsset = selectedAssetId ? assets.find(a => a.id === selectedAssetId) : null;

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left Sidebar - Filters */}
      <div className="w-80 border-r bg-card shrink-0 overflow-y-auto">
        <div className="p-4 space-y-4">
            {/* Search */}
            <div>
              <label className="text-sm font-medium mb-2 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search assets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="text-sm font-medium mb-2 block">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="not-started">Not Started</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="flagged">Flagged</SelectItem>
                  <SelectItem value="missing">Missing</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Condition Filter */}
            <div>
              <label className="text-sm font-medium mb-2 block">Condition</label>
              <Select value={conditionFilter} onValueChange={setConditionFilter}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All Conditions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Conditions</SelectItem>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="attention">Attention</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Location Filter */}
            <div>
              <label className="text-sm font-medium mb-2 block">Location</label>
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All Locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {uniqueLocations.map(location => (
                    <SelectItem key={location} value={location}>
                      {location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="w-full gap-2"
              >
                <X className="h-4 w-4" />
                Clear Filters
              </Button>
            )}
          </div>
      </div>

      {/* Right Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-4">
          {/* Summary Metrics - Moved to main content area */}
          <div className="grid grid-cols-5 gap-4">
            <button
              onClick={() => handleMetricClick('total')}
              className={cn(
                "overflow-hidden border-0 rounded-lg transition-all",
                activeMetricFilter === 'total' && "ring-2 ring-gray-500"
              )}
            >
              <div className="relative h-24 p-4 bg-teal-100">
                <div className="flex items-center justify-between h-full">
                  <div className="flex flex-col justify-center text-left">
                    <p className="text-neutral-700 text-sm font-medium">Total Assets</p>
                    <p className="text-neutral-900 text-2xl font-bold mt-1">{metrics.total}</p>
                  </div>
                  <div className="text-teal-600">
                    <CheckCircle2 className="h-10 w-10" strokeWidth={1.5} />
                  </div>
                </div>
              </div>
              <div className="p-4 bg-white">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-neutral-500">Assets assigned to audit</span>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleMetricClick('verified')}
              className={cn(
                "overflow-hidden border-0 rounded-lg transition-all",
                activeMetricFilter === 'verified' && "ring-2 ring-green-500"
              )}
            >
              <div className="relative h-24 p-4 bg-success-100">
                <div className="flex items-center justify-between h-full">
                  <div className="flex flex-col justify-center text-left">
                    <p className="text-neutral-700 text-sm font-medium">Verified</p>
                    <p className="text-neutral-900 text-2xl font-bold mt-1">{metrics.verified}</p>
                  </div>
                  <div className="text-success-600">
                    <CheckCircle2 className="h-10 w-10" strokeWidth={1.5} />
                  </div>
                </div>
              </div>
              <div className="p-4 bg-white">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-emerald-600">
                    {metrics.total > 0 ? Math.round((metrics.verified / metrics.total) * 100) : 0}%
                  </span>
                  <span className="text-sm text-neutral-500">completed</span>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleMetricClick('in-progress')}
              className={cn(
                "overflow-hidden border-0 rounded-lg transition-all",
                activeMetricFilter === 'in-progress' && "ring-2 ring-purple-500"
              )}
            >
              <div className="relative h-24 p-4 bg-teal-100">
                <div className="flex items-center justify-between h-full">
                  <div className="flex flex-col justify-center text-left">
                    <p className="text-neutral-700 text-sm font-medium">In Progress</p>
                    <p className="text-neutral-900 text-2xl font-bold mt-1">{metrics.inProgress}</p>
                  </div>
                  <div className="text-teal-600">
                    <Clock className="h-10 w-10" strokeWidth={1.5} />
                  </div>
                </div>
              </div>
              <div className="p-4 bg-white">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-emerald-600">
                    {metrics.total > 0 ? Math.round((metrics.inProgress / metrics.total) * 100) : 0}%
                  </span>
                  <span className="text-sm text-neutral-500">in progress</span>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleMetricClick('not-started')}
              className={cn(
                "overflow-hidden border-0 rounded-lg transition-all",
                activeMetricFilter === 'not-started' && "ring-2 ring-blue-500"
              )}
            >
              <div className="relative h-24 p-4 bg-teal-100">
                <div className="flex items-center justify-between h-full">
                  <div className="flex flex-col justify-center text-left">
                    <p className="text-neutral-700 text-sm font-medium">Not Started</p>
                    <p className="text-neutral-900 text-2xl font-bold mt-1">{metrics.notStarted}</p>
                  </div>
                  <div className="text-teal-600">
                    <Clock className="h-10 w-10" strokeWidth={1.5} />
                  </div>
                </div>
              </div>
              <div className="p-4 bg-white">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-emerald-600">
                    {metrics.total > 0 ? Math.round((metrics.notStarted / metrics.total) * 100) : 0}%
                  </span>
                  <span className="text-sm text-neutral-500">pending</span>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleMetricClick('flagged')}
              className={cn(
                "overflow-hidden border-0 rounded-lg transition-all",
                activeMetricFilter === 'flagged' && "ring-2 ring-orange-500"
              )}
            >
              <div className="relative h-24 p-4 bg-warning-100">
                <div className="flex items-center justify-between h-full">
                  <div className="flex flex-col justify-center text-left">
                    <p className="text-neutral-700 text-sm font-medium">Flagged</p>
                    <p className="text-neutral-900 text-2xl font-bold mt-1">{metrics.flagged}</p>
                  </div>
                  <div className="text-warning-600">
                    <Flag className="h-10 w-10" strokeWidth={1.5} />
                  </div>
                </div>
              </div>
              <div className="p-4 bg-white">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-emerald-600">
                    {metrics.total > 0 ? Math.round((metrics.flagged / metrics.total) * 100) : 0}%
                  </span>
                  <span className="text-sm text-neutral-500">flagged</span>
                </div>
              </div>
            </button>
          </div>

          {/* Results Count */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {filteredAssets.length} of {assets.length} assets
            </p>
          </div>

          {/* Asset Cards */}
          {loading ? (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-spin" />
              <h3 className="text-lg font-semibold mb-2">Loading audit items...</h3>
              <p className="text-muted-foreground">Please wait while we load the audit data.</p>
            </div>
          ) : filteredAssets.length > 0 ? (
            filteredAssets.map(asset => (
              <AssetProgressCard
                key={asset.id}
                asset={asset}
                onFlagForReview={handleFlagForReview}
                onAssignWorkItem={handleAssignWorkItem}
                onAddComment={handleAddComment}
                onViewDetails={handleViewDetails}
              />
            ))
          ) : (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No assets found</h3>
              <p className="text-muted-foreground">
                {hasActiveFilters
                  ? 'Try adjusting your filters to see more results.'
                  : 'No assets have been added to this audit yet.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      {selectedAsset && (
        <>
          <AssignWorkItemDialog
            open={isWorkItemDialogOpen}
            onOpenChange={setIsWorkItemDialogOpen}
            asset={selectedAsset}
            onSubmit={handleWorkItemSubmit}
          />

          <AddCommentDialog
            open={isCommentDialogOpen}
            onOpenChange={setIsCommentDialogOpen}
            assetCode={selectedAsset.assetCode}
            assetName={selectedAsset.assetName}
            onSubmit={handleCommentSubmit}
          />

          <AssetDetailsDialog
            open={isDetailsDialogOpen}
            onOpenChange={setIsDetailsDialogOpen}
            asset={selectedAsset}
          />
        </>
      )}
    </div>
  );
}
